import React from 'react';

import PropTypes from 'prop-types';
import { CSSProp } from 'styled-components';

import { variantSwitch } from '@adsk/alloy-react-helpers';
import { useTheme } from '@adsk/alloy-react-styles';
import theme, {
  stylePropType,
  StylableComponent,
  flattenStyle,
} from '@adsk/alloy-react-theme';

import {
  BADGE_VARIANTS,
  BADGE_SIZES,
  BADGE_COLORS,
  LINE_WITH_BG_OPACITY,
} from './consts';
import { Variant, Size } from './types';

const LINE_ACCENT_BAR_WIDTH = 3;
const LINE_PADDING_LEFT = LINE_ACCENT_BAR_WIDTH + theme.spacing.XS;

const lineWithBgStyles = {
  display: 'flex',
  borderRadius: theme.borderRadius.medium,
  paddingLeft: LINE_PADDING_LEFT,
  position: 'relative' as const,
  overflow: 'hidden',
};

type BadgeProps = StylableComponent<HTMLDivElement> & {
  css?: CSSProp;
  variant?: Variant;
  size?: Size;
  subStatus?: string | React.ReactNode;
  stacked?: boolean;
  color?: string;
  textColor?: string;
  disabled?: boolean;
};

/**
 * A simple component that renders a Badge based on a className/style passed
 */
const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      style,
      css,
      className,
      children,
      variant = BADGE_VARIANTS.SOLID,
      size = BADGE_SIZES.MEDIUM,
      color = BADGE_COLORS.DEFAULT,
      textColor: textColorProp,
      disabled,
      subStatus,
      stacked,
      ...props
    },
    ref,
  ) => {
    const badgeTokens = useTheme().tokens?.component?.badge;

    const resolvedTextColor = textColorProp ?? badgeTokens?.['text-color'];
    const primaryTextColor = badgeTokens?.['primary-text-color'];
    const disabledTextColor = badgeTokens?.disabled?.['text-color'];
    const disabledBgColor = badgeTokens?.disabled?.['background-color'];
    const disabledOpacity = badgeTokens?.disabled?.opacity;
    const outlineBorder = {
      borderWidth: badgeTokens?.outlined?.['border-width'],
      borderStyle: badgeTokens?.outlined?.['border-style'],
      borderColor: badgeTokens?.outlined?.['border-color'],
    };
    const secondaryTextColor = badgeTokens?.['secondary-text-color'];

    const paddingV = badgeTokens?.text?.['padding-top'];
    const paddingH = badgeTokens?.text?.['padding-left'];
    const minHeight = badgeTokens?.text?.height;
    const borderRadius = badgeTokens?.['border-radius'];

    const mediumTypography = {
      fontSize: badgeTokens?.['font-size'],
      fontWeight: badgeTokens?.['font-weight'],
      lineHeight: badgeTokens?.['line-height'],
      fontFamily: theme.typography.labelMedium.fontFamily,
    };

    const smallTypography = {
      fontSize: badgeTokens?.small?.['font-size'],
      fontWeight: badgeTokens?.small?.['font-weight'],
      lineHeight: badgeTokens?.small?.['line-height'],
      fontFamily: theme.typography.labelMedium.fontFamily,
    };

    const isLineVariant =
      variant === BADGE_VARIANTS.LINE ||
      variant === BADGE_VARIANTS.LINE_WITH_BG;
    const showSubStatus = isLineVariant && subStatus;

    return (
      <span
        className={className}
        ref={ref}
        style={flattenStyle(style)}
        css={[
          {
            padding: `${paddingV} ${paddingH}`,
            borderRadius,
            display: 'inline-flex',
            alignItems: 'center',
            minHeight,
            boxSizing: 'border-box',
            color: primaryTextColor,
            ...(disabled && {
              ...(disabledTextColor && { color: disabledTextColor }),
              opacity: disabledOpacity,
            }),
          },
          variantSwitch({
            statement: variant,
            variants: {
              [BADGE_VARIANTS.SOLID]: {
                color: resolvedTextColor,
                backgroundColor: color,
                ...(disabled && {
                  ...(disabledTextColor && { color: disabledTextColor }),
                  ...(disabledBgColor && { backgroundColor: disabledBgColor }),
                }),
              },
              [BADGE_VARIANTS.OUTLINE]: outlineBorder,
              [BADGE_VARIANTS.LINE]: {
                display: 'flex',
                minHeight: 'auto',
              },
              [BADGE_VARIANTS.LINE_WITH_BG]: lineWithBgStyles,
            },
          }),
          variantSwitch({
            statement: size,
            variants: {
              [BADGE_SIZES.SMALL]: smallTypography,
              [BADGE_SIZES.MEDIUM]: mediumTypography,
            },
          }),
          css,
        ]}
        {...props}
      >
        {variant === BADGE_VARIANTS.LINE && (
          <span
            css={[
              {
                minWidth: 4,
                width: 4,
                alignSelf: 'stretch',
                transform: 'scaleX(0.75)',
                borderRadius: 4,
                marginRight: theme.spacing.XS,
                backgroundColor: color,
                ...(disabled &&
                  disabledBgColor && {
                    backgroundColor: disabledBgColor,
                  }),
              },
            ]}
          />
        )}
        {variant === BADGE_VARIANTS.LINE_WITH_BG && (
          <span
            css={[
              {
                position: 'absolute' as const,
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                zIndex: 1,
              },
            ]}
          >
            <span
              css={[
                {
                  position: 'absolute' as const,
                  left: 0,
                  top: 2,
                  bottom: 2,
                  width: 3,
                  borderRadius: theme.borderRadius.medium,
                  overflow: 'hidden',
                },
              ]}
            >
              <span
                css={[
                  {
                    position: 'absolute' as const,
                    inset: -1,
                    backgroundColor: color,
                    ...(disabled &&
                      disabledBgColor && {
                        backgroundColor: disabledBgColor,
                      }),
                  },
                ]}
              />
            </span>
            <span
              css={[
                {
                  position: 'absolute' as const,
                  inset: 0,
                  backgroundColor: color,
                  opacity: LINE_WITH_BG_OPACITY,
                  ...(disabled &&
                    disabledBgColor && {
                      backgroundColor: disabledBgColor,
                    }),
                },
              ]}
            />
          </span>
        )}
        <span
          dir="auto"
          css={[
            {
              display: 'flex',
              flexDirection: stacked ? 'column' : 'row',
              alignItems: stacked ? 'flex-start' : 'baseline',
              ...(variant === BADGE_VARIANTS.LINE_WITH_BG && { zIndex: 2 }),
            },
          ]}
        >
          {children}
          {showSubStatus && (
            <span
              dir="auto"
              css={[
                {
                  color:
                    disabled && disabledTextColor
                      ? disabledTextColor
                      : secondaryTextColor,
                  fontFamily:
                    size === BADGE_SIZES.SMALL || stacked
                      ? badgeTokens?.substatus?.small?.['font-family']
                      : badgeTokens?.substatus?.['font-family'],
                  fontSize:
                    size === BADGE_SIZES.SMALL || stacked
                      ? badgeTokens?.substatus?.small?.['font-size']
                      : badgeTokens?.substatus?.['font-size'],
                  fontWeight:
                    size === BADGE_SIZES.SMALL || stacked
                      ? badgeTokens?.substatus?.small?.['font-weight']
                      : badgeTokens?.substatus?.['font-weight'],
                  lineHeight:
                    size === BADGE_SIZES.SMALL || stacked
                      ? badgeTokens?.substatus?.small?.['line-height']
                      : badgeTokens?.substatus?.['line-height'],
                },
                variantSwitch({
                  statement: String(!!stacked),
                  variants: {
                    false: {
                      paddingLeft: paddingH,
                    },
                    true: {},
                  },
                }),
              ]}
            >
              {subStatus}
            </span>
          )}
        </span>
      </span>
    );
  },
);

Badge.displayName = 'Badge';

Badge.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Badge style variant, defaults to 'solid' */
  variant: PropTypes.oneOf(Object.values(BADGE_VARIANTS)),
  /** Badge size, defaults to 'medium' */
  size: PropTypes.oneOf(Object.values(BADGE_SIZES)),
  /** Sub status to display, only in `LINE` and `LINE_WITH_BG` variants */
  subStatus: PropTypes.any,
  /** When using subStatus and set to true, the sub status is under the label */
  stacked: PropTypes.bool,
  /** Color to apply on the background of the Badge */
  color: PropTypes.string,
  /** Color to apply on the text */
  textColor: PropTypes.string,
  /** Sets the badge in disabled state */
  disabled: PropTypes.bool,
  /** The component's children, here you should put the text */
  children: PropTypes.any,
};

export default Object.assign(Badge, {
  VARIANTS: BADGE_VARIANTS,
  COLORS: BADGE_COLORS,
  SIZES: BADGE_SIZES,
});
